import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axiosInstance from '../utils/axiosConfig';
import {
  DEFAULT_ROLE_PERMISSIONS,
  isPrivilegedRole,
  normalizeModulePath
} from '../config/comingSoonRoutes';

const PermissionsContext = createContext();

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(DEFAULT_ROLE_PERMISSIONS);
  const [comingSoonRoutes, setComingSoonRoutes] = useState([]);
  const [muyProntoRoutes, setMuyProntoRoutes] = useState([]);
  // null = la clave aún no se sembró (migración 029): la regla de "módulo nuevo"
  // queda desactivada y todo se comporta como antes.
  const [knownRoutes, setKnownRoutes] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchPermissions = useCallback(async () => {
    try {
      const [permRes, csRes, mpRes, knownRes] = await Promise.all([
        axiosInstance.get('/api/settings/role-permissions'),
        axiosInstance.get('/api/settings/coming-soon'),
        axiosInstance.get('/api/settings/muy-pronto'),
        axiosInstance.get('/api/settings/known-modules'),
      ]);
      setPermissions({
        ...DEFAULT_ROLE_PERMISSIONS,
        ...(permRes.data.permissions || {})
      });
      setComingSoonRoutes(csRes.data.routes || []);
      setMuyProntoRoutes(mpRes.data.routes || []);
      setKnownRoutes(knownRes.data.routes ?? null);
    } catch (err) {
      console.error('Error al cargar permisos/visibilidad:', err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setPermissions(DEFAULT_ROLE_PERMISSIONS);
      setComingSoonRoutes([]);
      setMuyProntoRoutes([]);
      setKnownRoutes(null);
      setLoaded(false);
      return;
    }

    setLoaded(false);
    fetchPermissions();
  }, [user, fetchPermissions]);

  // Modulos de Procura: ademas del rol 'procura', el area de Procura (u
  // observador especial) tiene acceso, igual que Cuentas por Pagar con
  // viaticos. El backend manda ese flag en el usuario (ve_gestion_procura).
  const PROCURA_MODULE_PATHS = ['/requisicion-compras', '/solicitud-papeleria', '/solicitud-comida'];

  const hasModuleAccess = useCallback((user, path) => {
    const role = user?.role;
    if (!role) return false;
    if (isPrivilegedRole(role)) return true;
    const normalizedPath = normalizeModulePath(path);
    if (user?.ve_gestion_procura && PROCURA_MODULE_PATHS.includes(normalizedPath)) {
      return true;
    }
    const rolePaths = permissions[role];
    if (!rolePaths) return false;
    return rolePaths.includes(normalizedPath);
  }, [permissions]);

  // Un módulo recién desplegado (aún no decidido por un admin) se oculta solo,
  // para que un deploy no lo destape frente a todo el mundo.
  const isModuleNew = useCallback((path) => {
    if (knownRoutes === null) return false;
    return !knownRoutes.includes(normalizeModulePath(path));
  }, [knownRoutes]);

  const isModuleHidden = useCallback((path) => {
    const normalized = normalizeModulePath(path);
    return comingSoonRoutes.includes(normalized) || isModuleNew(normalized);
  }, [comingSoonRoutes, isModuleNew]);

  // 'Muy pronto': visible en el sidebar pero tapado al entrar.
  const isModuleMuyPronto = useCallback((path) => {
    const normalized = normalizeModulePath(path);
    return muyProntoRoutes.includes(normalized);
  }, [muyProntoRoutes]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        comingSoonRoutes,
        muyProntoRoutes,
        knownRoutes,
        hasModuleAccess,
        isModuleHidden,
        isModuleMuyPronto,
        isModuleNew,
        loaded,
        refetchPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions debe usarse dentro de PermissionsProvider');
  }
  return context;
}
